import React, { Component } from 'react';
import { Modal } from 'reactstrap';

// Visor de solo lectura para PDF e imagenes. No hay link al archivo: se pide por fetch
// (el backend solo responde con X-Requested-With) y se dibuja en canvas/img, sin capa de
// texto. Bloquea clic derecho, arrastrar, Ctrl/Cmd+S y Ctrl/Cmd+P y oculta la pagina al
// imprimir. Una captura de pantalla no se puede impedir.
//
// props: document ({ fileName, kind: "pdf" | "image" }), url, toggle

// pdf.js 2.16 legacy: el ultimo build que parsea webpack 4. pdf.worker.entry deja
// window.pdfjsWorker y pdf.js corre el worker en el hilo principal, sin archivo aparte.
// Se carga solo al abrir un PDF (chunk propio).
const loadPdfjs = () => Promise.all([
    import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/legacy/build/pdf'),
    import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/legacy/build/pdf.worker.entry'),
]).then(([pdfjs]) => (pdfjs.getDocument ? pdfjs : pdfjs.default));

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];
const BLOCKED_KEYS = ["s", "p"];

class DocumentViewer extends Component {
    constructor(props) {
        super(props)
        this.stageRef = React.createRef();
        this.pageRefs = {};
        this.rendered = {};
        this.tasks = {};
        this.pdf = null;
        this.closed = false;

        this.state = {
            status: "loading",
            message: "",
            scale: 1,
            fitScale: 1,
            pages: [],
            currentPage: 1,
            imageUrl: null,
            imageSize: null,
        }
    }

    componentDidMount() {
        document.body.classList.add("rk-viewer-open");
        document.addEventListener("keydown", this.blockKeys, true);

        if (["pdf", "image"].includes(this.props.document.kind)) {
            this.load();
        } else {
            this.setState({ status: "error", message: "Este documento no tiene vista previa." });
        }
    }

    componentWillUnmount() {
        this.closed = true;
        document.body.classList.remove("rk-viewer-open");
        document.removeEventListener("keydown", this.blockKeys, true);
        if (this.observer) this.observer.disconnect();
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
        Object.values(this.tasks).forEach(task => task.cancel());
        if (this.pdf) this.pdf.destroy();
        if (this.state.imageUrl) URL.revokeObjectURL(this.state.imageUrl);
    }

    blockKeys = (e) => {
        if ((e.ctrlKey || e.metaKey) && BLOCKED_KEYS.includes((e.key || "").toLowerCase())) {
            e.preventDefault();
            e.stopPropagation();
        }
    }

    blockEvent = (e) => {
        e.preventDefault();
    }

    load = () => {
        fetch(this.props.url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
            .then(response => {
                if (!response.ok) {
                    return response.json()
                        .catch(() => ({}))
                        .then(data => { throw new Error(data.success || "No se pudo abrir el documento."); });
                }
                const type = response.headers.get("Content-Type") || "";
                return response.arrayBuffer().then(buffer => ({ buffer, type }));
            })
            .then(({ buffer, type }) => {
                if (this.closed) return;
                return this.props.document.kind == "pdf" ? this.openPdf(buffer) : this.openImage(buffer, type);
            })
            .catch(error => {
                if (!this.closed) this.setState({ status: "error", message: error.message || "No se pudo abrir el documento." });
            });
    }

    // Ancho disponible del escenario, menos el padding.
    stageWidth = () => {
        const stage = this.stageRef.current;
        return stage ? stage.clientWidth - 48 : 800;
    }

    openImage = (buffer, type) => {
        const imageUrl = URL.createObjectURL(new Blob([buffer], { type }));
        this.setState({ imageUrl });
    }

    imageLoaded = (e) => {
        const imageSize = { width: e.target.naturalWidth, height: e.target.naturalHeight };
        const fitScale = Math.min(1, this.stageWidth() / imageSize.width);
        this.setState({ status: "ready", imageSize, scale: fitScale, fitScale });
    }

    imageFailed = () => {
        this.setState({ status: "error", message: "No se pudo mostrar la imagen." });
    }

    openPdf = (buffer) => {
        return loadPdfjs()
            // isEvalSupported: false cierra la ejecucion de codigo por fuentes del PDF (CVE-2024-4367).
            .then(pdfjs => pdfjs.getDocument({ data: new Uint8Array(buffer), isEvalSupported: false }).promise)
            .then(pdf => {
                if (this.closed) {
                    pdf.destroy();
                    return;
                }
                this.pdf = pdf;

                const sizes = [];
                for (let number = 1; number <= pdf.numPages; number++) {
                    sizes.push(pdf.getPage(number).then(page => {
                        const viewport = page.getViewport({ scale: 1 });
                        return { width: viewport.width, height: viewport.height };
                    }));
                }
                return Promise.all(sizes);
            })
            .then(pages => {
                if (!pages || this.closed) return;
                const fitScale = Math.min(1.5, Math.max(0.5, this.stageWidth() / pages[0].width));
                this.setState({ status: "ready", pages, scale: fitScale, fitScale }, this.observePages);
            });
    }

    // Solo se dibujan las paginas cerca de la vista; las que se alejan sueltan su canvas.
    observePages = () => {
        if (this.observer) this.observer.disconnect();
        this.rendered = {};

        this.observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                const number = Number(entry.target.dataset.page);
                if (entry.isIntersecting) {
                    this.renderPage(number);
                } else {
                    this.releasePage(number);
                }
            });
        }, { root: this.stageRef.current, rootMargin: "600px 0px" });

        Object.values(this.pageRefs).forEach(node => node && this.observer.observe(node));
    }

    renderPage = (number) => {
        const scale = this.state.scale;
        const node = this.pageRefs[number];
        if (!this.pdf || !node || this.rendered[number] === scale) return;

        this.rendered[number] = scale;
        if (this.tasks[number]) this.tasks[number].cancel();

        this.pdf.getPage(number)
            .then(page => {
                if (this.closed || this.state.scale !== scale) return;

                const canvas = node.querySelector("canvas");
                const ratio = window.devicePixelRatio || 1;
                const viewport = page.getViewport({ scale: scale * ratio });
                canvas.width = Math.floor(viewport.width);
                canvas.height = Math.floor(viewport.height);

                const task = page.render({ canvasContext: canvas.getContext("2d"), viewport });
                this.tasks[number] = task;
                return task.promise.then(() => {
                    if (this.tasks[number] === task) delete this.tasks[number];
                });
            })
            .catch(error => {
                // Cancelar un render (zoom o scroll rapido) no es un error.
                if (!error || error.name !== "RenderingCancelledException") delete this.rendered[number];
            });
    }

    releasePage = (number) => {
        if (this.tasks[number]) {
            this.tasks[number].cancel();
            delete this.tasks[number];
        }
        delete this.rendered[number];

        const node = this.pageRefs[number];
        const canvas = node && node.querySelector("canvas");
        if (canvas) {
            canvas.width = 0;
            canvas.height = 0;
        }
    }

    setScale = (scale) => {
        if (scale == this.state.scale) return;

        // Conserva la posicion relativa de lectura al cambiar el zoom.
        const stage = this.stageRef.current;
        const position = stage && stage.scrollHeight > 0 ? stage.scrollTop / stage.scrollHeight : 0;

        Object.values(this.tasks).forEach(task => task.cancel());
        this.tasks = {};

        this.setState({ scale }, () => {
            if (stage) stage.scrollTop = position * stage.scrollHeight;
            if (this.pdf) this.observePages();
        });
    }

    zoomIn = () => {
        const next = ZOOM_STEPS.find(step => step > this.state.scale + 0.01);
        if (next) this.setScale(next);
    }

    zoomOut = () => {
        const previous = [...ZOOM_STEPS].reverse().find(step => step < this.state.scale - 0.01);
        if (previous) this.setScale(previous);
    }

    handleScroll = () => {
        if (!this.pdf || this.scrollFrame) return;

        this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            const stage = this.stageRef.current;
            if (!stage) return;

            const mark = stage.scrollTop + stage.clientHeight / 3;
            let currentPage = 1;
            for (let number = 1; number <= this.state.pages.length; number++) {
                const node = this.pageRefs[number];
                if (node && node.offsetTop <= mark) currentPage = number;
            }
            if (currentPage != this.state.currentPage) this.setState({ currentPage });
        });
    }

    renderPdf() {
        const { pages, scale } = this.state;

        return pages.map((page, index) => (
            <div
                key={index + 1}
                data-page={index + 1}
                ref={node => { this.pageRefs[index + 1] = node; }}
                className="rk-viewer__page"
                style={{ width: page.width * scale, height: page.height * scale }}
            >
                <canvas />
            </div>
        ));
    }

    renderImage() {
        const { imageUrl, imageSize, scale } = this.state;
        if (!imageUrl) return null;

        return (
            <div className="rk-viewer__page" style={imageSize ? { width: imageSize.width * scale } : { visibility: "hidden", position: "absolute" }}>
                <img src={imageUrl} alt="" draggable={false} onLoad={this.imageLoaded} onError={this.imageFailed} />
            </div>
        );
    }

    render() {
        const { status, message, scale, fitScale, pages, currentPage } = this.state;
        const doc = this.props.document;
        const isPdf = doc.kind == "pdf";
        const ready = status == "ready";

        return (
            <Modal
                isOpen={true}
                toggle={this.props.toggle}
                className="modal-dialog-centered modal-xl rk-modal rk-viewer"
                backdrop={"static"}
            >
                <div className="rk-modal__header">
                    <span className="rk-modal__header-icon">
                        <i className={isPdf ? "fas fa-file-pdf" : "fas fa-file-image"}></i>
                    </span>
                    <h5 className="rk-viewer__title" title={doc.fileName}>{doc.fileName}</h5>

                    {ready && (
                        <div className="rk-viewer__tools">
                            {isPdf && (
                                <span className="rk-viewer__pages">Página {currentPage} de {pages.length}</span>
                            )}
                            <button type="button" className="rk-icon-btn" onClick={this.zoomOut} disabled={scale <= ZOOM_STEPS[0]} title="Alejar">
                                <i className="fas fa-search-minus"></i>
                            </button>
                            <span className="rk-viewer__zoom">{Math.round(scale * 100)}%</span>
                            <button type="button" className="rk-icon-btn" onClick={this.zoomIn} disabled={scale >= ZOOM_STEPS[ZOOM_STEPS.length - 1]} title="Acercar">
                                <i className="fas fa-search-plus"></i>
                            </button>
                            <button type="button" className="rk-icon-btn" onClick={() => this.setScale(fitScale)} title="Ajustar al ancho">
                                <i className="fas fa-arrows-alt-h"></i>
                            </button>
                        </div>
                    )}

                    <button type="button" className="rk-modal__close" onClick={() => this.props.toggle()}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div
                    className="rk-viewer__stage"
                    ref={this.stageRef}
                    onScroll={this.handleScroll}
                    onContextMenu={this.blockEvent}
                    onDragStart={this.blockEvent}
                >
                    {status == "error" ? (
                        <div className="rk-empty">
                            <i className="fas fa-eye-slash"></i>
                            <p>{message}</p>
                        </div>
                    ) : (
                        <React.Fragment>
                            {status == "loading" && (
                                <div className="rk-empty">
                                    <i className="fas fa-spinner fa-spin"></i>
                                    <p>Abriendo documento…</p>
                                </div>
                            )}
                            {isPdf ? this.renderPdf() : this.renderImage()}
                        </React.Fragment>
                    )}
                </div>

                <div className="rk-modal__footer rk-viewer__footer">
                    <span className="rk-viewer__note">
                        <i className="fas fa-lock"></i> Documento de solo consulta: no se puede descargar ni imprimir
                    </span>
                    <button type="button" className="rk-btn rk-btn--ghost" onClick={() => this.props.toggle()}>Cerrar</button>
                </div>
            </Modal>
        );
    }
}

export default DocumentViewer;
